import { isSavedRecordingUnavailable } from "@/services/recordingOwnership";

import {
  ASSEMBLYAI_RECOVERY_DB_NAME,
  isAssemblyAIScribe,
} from "@constants/live-transcription";

import { getCurrentUserKey } from "@utils/appToken";
import {
  registerRecoveryDisowner,
  registerSavedRecordingCheck,
} from "@utils/clearRecordingRecovery";

import type { SpeakerHint } from "../components/SpeakerChecklist";
import type { SessionMode } from "../types/sessionMode";

// Case- and user-scoped: never disclose another case's or user's interrupted session (PHI).
// Best-effort: every failure is swallowed so recording is never blocked.
// One recording (audio chunks) per session; one transcript row per connection segment.
const DB_NAME = ASSEMBLYAI_RECOVERY_DB_NAME;
const DB_VERSION = 2;
const CHUNK_STORE = "chunks";
const SEGMENT_STORE = "segments";
const LEGACY_FEED_STORE = "feeds";
const MAX_ORPHAN_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// MediaRecorder timeslice: one chunk per recorded second.
const CHUNK_MS = 1000;

type SessionMeta = {
  submissionId?: string;
  sessionId: string;
  caseNumber: string | null;
  // Absent on records written before user scoping; treated as null.
  userId?: string | null;
  examineeName: string | null;
  mimeType: string;
  startedAt: number;
  speakerHints?: SpeakerHint[];
  mode?: SessionMode;
  micDeviceId?: string;
};

type PersistedChunk = SessionMeta & {
  id?: number;
  // Not every IndexedDB impl round-trips a Blob intact.
  data: ArrayBuffer;
};

export type RecoveredFeedRow = {
  text: string;
  timestamp: string;
  speaker?: string;
  divider?: boolean;
};

type PersistedSegment = SessionMeta & {
  segmentId: string;
  index: number;
  rows: RecoveredFeedRow[];
  // Recorded time of the whole session at this snapshot.
  recordedMs: number;
  updatedAt: number;
  // Set on the marker written when the doctor stopped (rather than lost) the recording.
  stopped?: boolean;
};

export type RecoveredSegment = {
  index: number;
  rows: RecoveredFeedRow[];
  recordedMs: number;
};

export type RecoveredSession = {
  submissionId?: string;
  sessionId: string;
  caseNumber: string | null;
  userId: string | null;
  examineeName: string | null;
  mimeType: string;
  startedAt: number;
  speakerHints: SpeakerHint[];
  mode: SessionMode;
  micDeviceId?: string;
  blob: Blob | null;
  chunks: Blob[];
  segments: RecoveredSegment[];
  // All segments combined, with a divider between them.
  feed: RecoveredFeedRow[] | null;
  // Recorded time to continue the clock and transcript timeline from.
  resumeOffsetMs: number;
  // Stopped on purpose but not yet accepted for transcription (MDRS-551), not interrupted.
  stopped: boolean;
};

type Owner = SessionMeta & { segmentIndex: number };

let owner: Owner | null = null;

const segmentIdOf = (sessionId: string, index: number) => `${sessionId}:${index}`;

// Coalesced: each snapshot is complete, so only the latest waiting one is kept.
let pendingFeed: {
  segmentId: string;
  rows: RecoveredFeedRow[];
  recordedMs: number;
} | null = null;
let feedWriteLoop: Promise<void> | null = null;
const chunkWrites = new Set<Promise<void>>();

const hasIndexedDb = () => typeof indexedDB !== "undefined";

const openDb = (): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    if (!hasIndexedDb()) {
      resolve(null);
      return;
    }
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(CHUNK_STORE)) {
          db.createObjectStore(CHUNK_STORE, { keyPath: "id", autoIncrement: true });
        }
        if (db.objectStoreNames.contains(LEGACY_FEED_STORE)) {
          db.deleteObjectStore(LEGACY_FEED_STORE);
        }
        if (!db.objectStoreNames.contains(SEGMENT_STORE)) {
          db.createObjectStore(SEGMENT_STORE, { keyPath: "segmentId" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

const runRequest = <T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T | null> =>
  new Promise((resolve) => {
    try {
      const tx = db.transaction(storeName, mode);
      const request = makeRequest(tx.objectStore(storeName));
      let result: T | null = null;
      request.onsuccess = () => {
        result = request.result;
      };
      request.onerror = () => {
        /* tx.onerror below still fires and resolves */
      };
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

const deleteMatching = <R>(
  db: IDBDatabase,
  storeName: string,
  predicate: (record: R) => boolean
): Promise<void> =>
  new Promise((resolve) => {
    try {
      const tx = db.transaction(storeName, "readwrite");
      const cursorRequest = tx.objectStore(storeName).openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return;
        if (predicate(cursor.value as R)) cursor.delete();
        cursor.continue();
      };
      cursorRequest.onerror = () => {
        /* tx.onerror below still fires and resolves */
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });

const deleteFromBothStores = async (
  db: IDBDatabase,
  predicate: (record: SessionMeta) => boolean
): Promise<void> => {
  await deleteMatching<SessionMeta>(db, CHUNK_STORE, predicate);
  await deleteMatching<SessionMeta>(db, SEGMENT_STORE, predicate);
};

const ownerSegmentId = () =>
  owner ? segmentIdOf(owner.sessionId, owner.segmentIndex) : null;

const writeFeed = async (
  segmentId: string,
  rows: RecoveredFeedRow[],
  recordedMs: number
): Promise<void> => {
  const claimed = owner;
  if (!claimed || ownerSegmentId() !== segmentId) return;
  const db = await openDb();
  if (!db) return;
  try {
    // A teardown may have run during openDb(); a stray write would look orphaned.
    if (ownerSegmentId() !== segmentId) return;
    const { segmentIndex, ...meta } = claimed;
    const record: PersistedSegment = {
      ...meta,
      segmentId,
      index: segmentIndex,
      rows,
      recordedMs,
      updatedAt: Date.now(),
    };
    await runRequest(db, SEGMENT_STORE, "readwrite", (store) => store.put(record));
  } finally {
    db.close();
  }
};

const drainFeedWrites = async (): Promise<void> => {
  try {
    while (pendingFeed) {
      const next = pendingFeed;
      pendingFeed = null;
      await writeFeed(next.segmentId, next.rows, next.recordedMs).catch(() => {
        /* best-effort */
      });
    }
  } finally {
    feedWriteLoop = null;
  }
};

export const beginPersistedRecording = async (
  meta: Omit<SessionMeta, "startedAt" | "userId">
): Promise<void> => {
  owner = {
    ...meta,
    userId: getCurrentUserKey(),
    startedAt: Date.now(),
    segmentIndex: 0,
  };
  const db = await openDb();
  if (!db) return;
  try {
    // Deliberately any user's: one orphan per case, even on a shared machine.
    await deleteFromBothStores(
      db,
      (r) => r.caseNumber === meta.caseNumber && r.sessionId !== meta.sessionId
    );
  } finally {
    db.close();
  }
};

// Continues an orphaned recording: same session (audio keeps appending), next segment.
export const resumePersistedRecording = (recovered: RecoveredSession): void => {
  const lastIndex = recovered.segments.reduce(
    (max, s) => Math.max(max, s.index),
    -1
  );
  owner = {
    submissionId: recovered.submissionId,
    sessionId: recovered.sessionId,
    caseNumber: recovered.caseNumber,
    userId: recovered.userId,
    examineeName: recovered.examineeName,
    mimeType: recovered.mimeType,
    startedAt: recovered.startedAt,
    speakerHints: recovered.speakerHints,
    mode: recovered.mode,
    ...(recovered.micDeviceId ? { micDeviceId: recovered.micDeviceId } : {}),
    segmentIndex: lastIndex + 1,
  };
};

// Tracked so logout and suspend can wait for a write already past its ownership checks.
export const persistRecordingChunk = (
  sessionId: string,
  chunk: Blob
): Promise<void> => {
  const write = writeChunk(sessionId, chunk).finally(() =>
    chunkWrites.delete(write)
  );
  chunkWrites.add(write);
  return write;
};

const writeChunk = async (sessionId: string, chunk: Blob): Promise<void> => {
  if (owner?.sessionId !== sessionId) return;
  if (!chunk || chunk.size === 0) return;
  const data = await chunk.arrayBuffer();
  const claimed = owner;
  if (claimed?.sessionId !== sessionId) return;
  const db = await openDb();
  if (!db) return;
  try {
    if (owner?.sessionId !== sessionId) return;
    const { segmentIndex: _segmentIndex, ...meta } = claimed;
    const record: PersistedChunk = { ...meta, data };
    await runRequest(db, CHUNK_STORE, "readwrite", (store) => store.add(record));
  } finally {
    db.close();
  }
};

// Deliberately dropped (not buffered) when unowned: a late SpeakerRevision
// would otherwise be saved under the next session, possibly another case (PHI).
// Writes only the current segment's rows.
export const persistLiveFeedForActiveSession = (
  segments: RecoveredFeedRow[],
  recordedMs = 0
): Promise<void> => {
  const segmentId = ownerSegmentId();
  if (!segmentId) return Promise.resolve();
  pendingFeed = {
    segmentId,
    recordedMs,
    rows: segments.map(({ text, timestamp, speaker, divider }) => ({
      text,
      timestamp,
      ...(speaker ? { speaker } : {}),
      ...(divider ? { divider } : {}),
    })),
  };
  feedWriteLoop ??= drainFeedWrites();
  return feedWriteLoop;
};

const hasSpeech = (rows: RecoveredFeedRow[]) =>
  rows.some((row) => !row.divider && row.text.trim());

// Empty segments are skipped so a resume with no speech adds no divider.
export const combineSegmentRows = (
  segments: Pick<RecoveredSegment, "rows">[]
): RecoveredFeedRow[] => {
  const combined: RecoveredFeedRow[] = [];
  for (const segment of segments) {
    if (!hasSpeech(segment.rows)) continue;
    const last = combined[combined.length - 1];
    if (last && !last.divider) {
      combined.push({ text: "", timestamp: "", divider: true });
    }
    combined.push(...segment.rows);
  }
  return combined;
};

export const formatRecoveredFeed = (rows: RecoveredFeedRow[]): string =>
  rows
    .map((row) => {
      if (row.divider) return "— Reconnected (speaker labels may restart) —";
      const stamp = row.timestamp ? `[${row.timestamp}] ` : "";
      const who = row.speaker ? `Speaker ${row.speaker}: ` : "";
      return `${stamp}${who}${row.text.trim()}`;
    })
    .filter((line) => line.trim().length > 0)
    .join("\n");

/**
 * Stop (MDRS-551): marks the kept recording as finished, so recovery offers to
 * send it rather than resume it. Call before `releasePersistedRecording`, which
 * waits for this write; it adds no transcript rows or audio.
 */
export const markPersistedRecordingStopped = (sessionId: string): Promise<void> => {
  const claimed = owner;
  if (claimed?.sessionId !== sessionId) return Promise.resolve();
  const write = writeStoppedMarker(claimed).finally(() => chunkWrites.delete(write));
  chunkWrites.add(write);
  return write;
};

const writeStoppedMarker = async ({
  segmentIndex,
  ...meta
}: Owner): Promise<void> => {
  const db = await openDb();
  if (!db) return;
  try {
    const record: PersistedSegment = {
      ...meta,
      segmentId: `${meta.sessionId}:stopped`,
      index: segmentIndex + 1,
      rows: [],
      recordedMs: 0,
      updatedAt: Date.now(),
      stopped: true,
    };
    await runRequest(db, SEGMENT_STORE, "readwrite", (store) => store.put(record));
  } finally {
    db.close();
  }
};

/** Resolves once every chunk and feed write already started has landed. */
export const waitForPendingWrites = async (): Promise<void> => {
  await Promise.all([...chunkWrites].map((w) => w.catch(() => {})));
  await feedWriteLoop;
};

/**
 * Suspend or Stop: keeps the records (for the Resume prompt, or until the
 * summarize request is accepted — MDRS-551), but stops writing to them once
 * in-flight writes land. With a `sessionId`, leaves a newer session's ownership alone.
 */
export const releasePersistedRecording = async (
  sessionId?: string
): Promise<void> => {
  await waitForPendingWrites();
  if (sessionId && owner?.sessionId !== sessionId) return;
  owner = null;
  pendingFeed = null;
};

// Logout: stop all writes (chunks and feed) so the DB delete that follows sticks.
const disownPersistedRecording = async (): Promise<void> => {
  owner = null;
  pendingFeed = null;
  await waitForPendingWrites();
};
registerRecoveryDisowner(disownPersistedRecording);

export const clearPersistedRecording = async (sessionId: string): Promise<void> => {
  if (owner?.sessionId === sessionId) owner = null;
  if (pendingFeed?.segmentId.startsWith(`${sessionId}:`)) pendingFeed = null;
  // Let in-flight writes (feed, chunks, the stopped marker) land or bail first,
  // so none can land after the delete.
  await waitForPendingWrites();
  const db = await openDb();
  if (!db) return;
  try {
    await deleteFromBothStores(db, (r) => r.sessionId === sessionId);
  } finally {
    db.close();
  }
};

// Opening creates the DB, so with the flag off only look if an earlier session made one.
const recoveryDbExists = async (): Promise<boolean> => {
  if (!hasIndexedDb() || typeof indexedDB.databases !== "function") return false;
  try {
    const dbs = await indexedDB.databases();
    return dbs.some((d) => d.name === DB_NAME);
  } catch {
    return false;
  }
};

// Stops at the first hit so it never loads every saved chunk's audio into memory.
const anyMatching = <R>(
  db: IDBDatabase,
  storeName: string,
  predicate: (record: R) => boolean
): Promise<boolean> =>
  new Promise((resolve) => {
    try {
      const cursorRequest = db
        .transaction(storeName, "readonly")
        .objectStore(storeName)
        .openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return resolve(false);
        if (predicate(cursor.value as R)) return resolve(true);
        cursor.continue();
      };
      cursorRequest.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });

/**
 * Whether the store still holds audio or transcript worth sending for ANY case
 * or user (MDRS-551). Logout deletes the whole store, so it asks this first —
 * e.g. after a refresh, when nothing is in memory. Past-retention records don't count.
 */
// null when storage can't answer, so a transient error never reads as "gone".
const firstMatch = <R>(
  db: IDBDatabase,
  storeName: string,
  predicate: (record: R) => boolean
): Promise<boolean | null> =>
  new Promise((resolve) => {
    try {
      const cursorRequest = db
        .transaction(storeName, "readonly")
        .objectStore(storeName)
        .openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return resolve(false);
        if (predicate(cursor.value as R)) return resolve(true);
        cursor.continue();
      };
      cursorRequest.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

/** Whether this saved session still exists here; null when that can't be checked. */
export const savedSessionExists = async (
  sessionId: string
): Promise<boolean | null> => {
  const db = await openDb();
  if (!db) return null;
  try {
    const mine = (r: SessionMeta) => r.sessionId === sessionId;
    const segments = await firstMatch<SessionMeta>(db, SEGMENT_STORE, mine);
    if (segments !== false) return segments;
    return await firstMatch<SessionMeta>(db, CHUNK_STORE, mine);
  } finally {
    db.close();
  }
};

export const hasSavedRecordings = async (): Promise<boolean> => {
  if (!isAssemblyAIScribe() && !(await recoveryDbExists())) return false;
  const db = await openDb();
  if (!db) return false;
  try {
    const now = Date.now();
    const isRecent = (r: SessionMeta) => now - r.startedAt <= MAX_ORPHAN_AGE_MS;
    return (
      (await anyMatching<PersistedChunk>(
        db,
        CHUNK_STORE,
        (r) => isRecent(r) && r.data?.byteLength > 0
      )) ||
      (await anyMatching<PersistedSegment>(
        db,
        SEGMENT_STORE,
        (r) => isRecent(r) && hasSpeech(r.rows)
      ))
    );
  } finally {
    db.close();
  }
};
registerSavedRecordingCheck(hasSavedRecordings);

export const findOrphanedSession = async (
  caseNumber: string | null
): Promise<RecoveredSession | null> => {
  if (await isSavedRecordingUnavailable()) return null;
  if (!isAssemblyAIScribe() && !(await recoveryDbExists())) return null;
  const db = await openDb();
  if (!db) return null;
  try {
    const chunks =
      (await runRequest(
        db,
        CHUNK_STORE,
        "readonly",
        (store) => store.getAll() as IDBRequest<PersistedChunk[]>
      )) ?? [];
    const segments =
      (await runRequest(
        db,
        SEGMENT_STORE,
        "readonly",
        (store) => store.getAll() as IDBRequest<PersistedSegment[]>
      )) ?? [];
    if (chunks.length === 0 && segments.length === 0) return null;

    const now = Date.now();
    const staleSessionIds = new Set(
      [...chunks, ...segments]
        .filter((r) => now - r.startedAt > MAX_ORPHAN_AGE_MS)
        .map((r) => r.sessionId)
    );
    if (staleSessionIds.size > 0) {
      await deleteFromBothStores(db, (r) => staleSessionIds.has(r.sessionId));
    }

    // No identity means we can't prove ownership, so disclose nothing.
    const userId = getCurrentUserKey();
    if (!userId) return null;
    const isLiveForCase = (r: SessionMeta) =>
      r.caseNumber === caseNumber &&
      r.userId === userId &&
      !staleSessionIds.has(r.sessionId);
    const candidates: SessionMeta[] = [
      ...chunks.filter(isLiveForCase),
      ...segments.filter(isLiveForCase),
    ];
    if (candidates.length === 0) return null;

    const meta = candidates.reduce((a, b) => (b.startedAt > a.startedAt ? b : a));
    const sessionChunks = chunks.filter((r) => r.sessionId === meta.sessionId);
    const chunkBlobs = sessionChunks
      .filter((r) => r.data && r.data.byteLength > 0)
      .map((r) => new Blob([r.data], { type: meta.mimeType }));
    const blob =
      chunkBlobs.length > 0 ? new Blob(chunkBlobs, { type: meta.mimeType }) : null;
    const sessionSegments = segments
      .filter((r) => r.sessionId === meta.sessionId)
      .sort((a, b) => a.index - b.index)
      .map(({ index, rows, recordedMs }) => ({ index, rows, recordedMs }));
    const combined = combineSegmentRows(sessionSegments);
    const feed = hasSpeech(combined) ? combined : null;

    const usableBlob = blob && blob.size > 0 ? blob : null;
    if (!usableBlob && !feed) return null;

    const settings =
      [
        ...sessionChunks,
        ...segments.filter((r) => r.sessionId === meta.sessionId),
      ].find((r) => r.speakerHints || r.mode) ?? meta;
    const recordedMs = sessionSegments.reduce(
      (max, s) => Math.max(max, s.recordedMs),
      0
    );
    const stopped = segments.some(
      (r) => r.sessionId === meta.sessionId && r.stopped === true
    );

    return {
      submissionId: meta.submissionId,
      sessionId: meta.sessionId,
      caseNumber,
      userId,
      examineeName: meta.examineeName,
      mimeType: meta.mimeType,
      startedAt: meta.startedAt,
      speakerHints: settings.speakerHints ?? [],
      mode: settings.mode ?? "in_person",
      ...(settings.micDeviceId ? { micDeviceId: settings.micDeviceId } : {}),
      blob: usableBlob,
      chunks: usableBlob ? chunkBlobs : [],
      segments: sessionSegments,
      feed,
      resumeOffsetMs: Math.max(recordedMs, chunkBlobs.length * CHUNK_MS),
      stopped,
    };
  } finally {
    db.close();
  }
};

export const resetAssemblyAIRecoveryForTest = (): void => {
  owner = null;
  pendingFeed = null;
  feedWriteLoop = null;
};

export const DB_NAME_FOR_TEST = DB_NAME;
export const MAX_ORPHAN_AGE_MS_FOR_TEST = MAX_ORPHAN_AGE_MS;
